import React, { useState } from 'react';
import { GenericTable } from '@shared/components/GenericTable';
import { Dialog, DialogContent, DialogTitle } from '@shared/ui/dialog';
import { Badge } from '@shared/ui/badge';
import { CreditCard, Eye, FileText, User } from 'lucide-react';
import { cn } from '@shared/lib/utils';

import { usePaymentList, usePaymentDetail } from '../../hook';
import { getRemoteUtilityId } from '@shared/utils/getUtilityId';
import { getLoginDataFromStorage } from '@shared/utils/loginUtils';
import { useCurrency } from '@shared/selectors/globalSelectors';
import { useTranslation } from 'react-i18next';

export const PaymentDetailsModal = ({ payment }: { payment: any }) => {
  const { t } = useTranslation();
  const currency = useCurrency();

  const { data: paymentDetailData } = usePaymentDetail({
    payment_id: payment?.id,
    remote_utility_id: getRemoteUtilityId(),
  });
  
  const paymentInfo = paymentDetailData?.result?.hero;

  if (!payment) return null;

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-start space-x-4 mb-2">
        <div className="bg-green-50 p-2.5 rounded-xl border border-green-100">
          <CreditCard className="w-6 h-6 text-green-600" />
        </div>
        <div>
          <DialogTitle className="text-lg font-bold text-gray-900 mt-1">{t("Payment Details")}</DialogTitle>
          <div className="text-sm text-muted-foreground mt-0.5">
            {t("Complete information about payment")} {paymentInfo?.receiptNo}
          </div>
        </div>
      </div>
      
      <div className="border border-gray-100 rounded-lg overflow-hidden shadow-sm">
        <div className="bg-gray-50/50 px-4 py-3 border-b border-gray-100">
          <span className="font-semibold text-gray-800 text-sm">{t("Payment Information")}</span>
        </div>
        <div className="p-5 grid grid-cols-2 gap-y-6 gap-x-4 bg-white">
          <div>
            <div className="text-[11px] text-muted-foreground mb-1.5 font-medium tracking-wider">{t("Payment Id")}</div>
            <div className="text-sm font-bold text-gray-900">{paymentInfo?.receiptNo}</div>
          </div>
          <div>
            <div className="text-[11px] text-muted-foreground mb-1.5 font-medium tracking-wider">{t("Amount")}</div>
            <div className="text-2xl font-bold text-green-600">{currency} {paymentInfo?.amount ?? 'NA'}</div>
          </div>
          <div>
            <div className="text-[11px] text-muted-foreground mb-1.5 font-medium tracking-wider">{t("Payment Date")}</div>
            <div className="text-sm font-semibold text-gray-900">{paymentInfo?.paymentDate || 'N/A'}</div>
          </div>
          <div>
            <div className="text-[11px] text-muted-foreground mb-1.5 font-medium tracking-wider">{t("Payment Method")}</div>
            <div className="text-sm font-semibold text-gray-900">{paymentInfo?.paymentMode || 'NA'}</div>
          </div>
          <div>
            <div className="text-[11px] text-muted-foreground mb-1.5 font-medium tracking-wider">{t("Status")}</div>
            <Badge variant="secondary" className={cn("font-medium", payment.paymentReceivedStatus === 'Posted' ? "bg-green-50 text-green-600 border-transparent hover:bg-green-50" : (payment.paymentReceivedStatus === 'Failed' ? "bg-red-50 text-red-600" : ""))}>
              {payment.paymentReceivedStatus || 'NA'}
            </Badge>
          </div>
          <div>
            <div className="text-[11px] text-muted-foreground mb-1.5 font-medium tracking-wider">{t("Payment Type")}</div>
            <Badge variant="outline" className="text-gray-700 border-gray-300 font-medium bg-white">
              <FileText className="w-3.5 h-3.5 mr-1.5 text-gray-500" />
              {payment.paymentPayTypeDisplay || 'NA'}
            </Badge>
          </div>
        </div>
      </div>

      <div className="border border-gray-100 rounded-lg overflow-hidden shadow-sm">
        <div className="bg-gray-50/50 px-4 py-3 border-b border-gray-100">
          <span className="font-semibold text-gray-800 text-sm">{t("Payment Details")}</span>
        </div>
        <div className="p-5 grid grid-cols-2 gap-y-6 gap-x-4 bg-white">
          <div>
            <div className="text-[11px] text-muted-foreground mb-1.5 font-medium tracking-wider">{t("Bill Number")}</div>
            <div className="text-sm font-semibold text-blue-600">{payment.remoteBillId || 'N/A'}</div>
          </div>
          <div>
            <div className="text-[11px] text-muted-foreground mb-1.5 font-medium tracking-wider">{t("Recorded By")}</div>
            <div className="flex items-center space-x-2 text-sm font-semibold text-gray-900">
              <User className="w-3.5 h-3.5 text-gray-400" />
              <span>{payment.createdUserRemoteName || 'NA'}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const PaymentHistory = () => {
  const { t } = useTranslation();
  const [selectedPayment, setSelectedPayment] = useState<any>(null);
  const [page, setPage] = useState(1);
  const limit = 20;
  
  const { consumerId } = getLoginDataFromStorage();

  const { data: paymentsData } = usePaymentList({
    remote_utility_id: getRemoteUtilityId(),
    consumer_id: consumerId,
    page,
    limit
  });

  const currency = useCurrency();
  const paymentRecords = paymentsData?.results;
  
  const totalCount = paymentsData?.count || paymentsData?.result?.count || 0;
  const totalPages = Math.ceil(totalCount / limit);
  const startItem = totalCount > 0 ? (page - 1) * limit + 1 : 0;
  const endItem = Math.min(page * limit, totalCount);

  const columns = React.useMemo(() => [
    { header: t("Receipt Id"), key: 'receiptNo' as const, render: (val: string, row: any) => val || row.id },
    { header: t("Date"), key: 'paymentDate' as const, render: (val: any) => val || 'N/A' },
    { header: t("Description"), key: 'paymentPayTypeDisplay' as const, render: (val: any) => val || 'Bill Payment' },
    { header: t("Amount"), key: 'amount' as const, render: (val: string) => <span className="font-bold">{currency} {Number(val || 0).toFixed(2)}</span> },
    { header: t("Method"), key: 'paymentMode' as const, render: (val: any) => val || 'Online' },
    { 
      header: t("Status"), 
      key: 'paymentReceivedStatus' as const,
      type: 'badge',
      badgeConfig: {
        getVariant: (value: string) => {
          if (value === 'Posted') return 'success';
          if (value === 'Failed') return 'destructive';
          if (value === 'In Progress') return 'warning';
          return 'default';
        },
        getClassName: (value: string) => {
          if (value === 'Posted') return 'bg-green-50 text-green-600 border-green-200 hover:bg-green-50 font-medium';
          return '';
        }
      }
    },
  ], [t, currency]);

  return (
    <div className="space-y-4">
      <GenericTable 
        rowKey="id"
        columns={columns} 
        data={paymentRecords}
        pagination={{
          currentPage: page,
          totalPages: totalPages || 1,
          startItem: startItem,
          endItem: endItem,
          totalItems: totalCount
        }}
        onPageChange={setPage}
        actions={[
          {
            label: t("View Details"),
            icon: <Eye className="w-4 h-4 text-gray-500" />,
            onClick: (row: any) => setSelectedPayment(row),
          }
        ]}
        actionsType="icons"
        onExport={() => console.log('Exporting...')}
      />

      <Dialog open={!!selectedPayment} onOpenChange={(open) => !open && setSelectedPayment(null)}>
        <DialogContent className="sm:max-w-xl p-0 border-none bg-white shadow-xl rounded-xl">
          <PaymentDetailsModal payment={selectedPayment} />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PaymentHistory;
